// src/pages/SuperadminPlataforma.js
//
// Dashboard consolidado para SUPERADMIN del POS Compacto.
//
// Muestra:
//   - Métricas globales: cuántas empresas contratadas, cuántas con POS,
//     cuántas con HolaVeci, cuántas con ambos, revenue global.
//   - Listado completo de empresas con estado, flags de productos y
//     métricas individuales del período.
//   - Filtros por contratación (POS, HolaVeci, ambos, solo POS, solo HV).
//   - Toggle rápido para activar/desactivar HolaVeci por empresa.
//
// Distinto al dashboard del admin_negocio (que ve solo su empresa).
// HolaVeci tiene su propio dashboard superadmin en la administración
// de HolaVeci — este es el equivalente para POS Compacto.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Stack, Chip, Grid, IconButton,
  ToggleButtonGroup, ToggleButton, Table, TableBody, TableCell, TableHead,
  TableRow, TableContainer, TextField, MenuItem, InputAdornment, LinearProgress,
  Alert, Switch, Tooltip, Paper,
} from '@mui/material';
import BusinessIcon        from '@mui/icons-material/Business';
import StorefrontIcon      from '@mui/icons-material/Storefront';
import LocalShippingIcon   from '@mui/icons-material/LocalShipping';
import PointOfSaleIcon     from '@mui/icons-material/PointOfSale';
import PaidIcon            from '@mui/icons-material/Paid';
import PeopleIcon          from '@mui/icons-material/People';
import RefreshIcon         from '@mui/icons-material/Refresh';
import SearchIcon          from '@mui/icons-material/Search';
import CheckCircleIcon     from '@mui/icons-material/CheckCircle';
import CancelIcon          from '@mui/icons-material/Cancel';
import { useApp } from '../context/AppContext';
import { adminPlataforma as apiSA } from '../api/posApi';

const fmtCOP = (n) => (Number(n) || 0).toLocaleString('es-CO',
  { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });
const fmtNum = (n) => (Number(n) || 0).toLocaleString('es-CO');

const MiniStat = ({ icon, label, value, color = 'text.primary' }) => (
  <Paper variant="outlined" sx={{ p: 2 }}>
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Box sx={{
        width: 42, height: 42, borderRadius: 2, bgcolor: color, opacity: 0.15,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>{React.cloneElement(icon, { sx: { color } })}</Box>
      <Box>
        <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
        <Typography variant="h6" fontWeight={700}>{value}</Typography>
      </Box>
    </Stack>
  </Paper>
);

const SuperadminPlataforma = () => {
  const { notify } = useApp();
  const [dias, setDias] = useState('30');
  const [resumen, setResumen] = useState(null);
  const [empresas, setEmpresas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [buscar, setBuscar] = useState('');
  const [contrata, setContrata] = useState('');
  const [tick, setTick] = useState(0);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [r, l] = await Promise.all([
        apiSA.resumen(parseInt(dias, 10)),
        apiSA.listarEmpresas({
          dias: parseInt(dias, 10),
          buscar: buscar.trim() || undefined,
          contrata: contrata || undefined,
        }),
      ]);
      setResumen(r);
      setEmpresas(Array.isArray(l?.empresas) ? Array.from(l.empresas) : []);
    } catch (e) {
      notify(e.mensajeUsuario || 'No se pudo cargar el consolidado.', 'error');
    } finally { setCargando(false); }
  }, [dias, buscar, contrata, notify]);

  useEffect(() => { cargar(); }, [cargar, tick]);

  // Debounce búsqueda
  useEffect(() => {
    const t = setTimeout(() => setTick(x => x + 1), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buscar]);

  const cambiarHolaVeci = async (e, nuevo) => {
    try {
      await apiSA.toggleHolaVeci(e.empresaId, nuevo);
      notify(`HolaVeci ${nuevo ? 'activado' : 'desactivado'} para ${e.razonSocial}.`, 'success');
      setTick(t => t + 1);
    } catch (err) { notify(err.mensajeUsuario || 'No se pudo actualizar.', 'error'); }
  };

  const cambiarPOS = async (e, nuevo) => {
    try {
      await apiSA.togglePOS(e.empresaId, nuevo);
      notify(`POS ${nuevo ? 'activado' : 'desactivado'} para ${e.razonSocial}.`, 'success');
      setTick(t => t + 1);
    } catch (err) { notify(err.mensajeUsuario || 'No se pudo actualizar.', 'error'); }
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between"
             alignItems={{ sm: 'center' }} gap={2} mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Consola Superadmin</Typography>
          <Typography color="text.secondary">
            Vista consolidada de todas las empresas contratadas de HolaVeci Local
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <ToggleButtonGroup value={dias} exclusive onChange={(_, v) => v && setDias(v)}
                             size="small" color="primary">
            <ToggleButton value="7">7d</ToggleButton>
            <ToggleButton value="15">15d</ToggleButton>
            <ToggleButton value="30">30d</ToggleButton>
            <ToggleButton value="90">90d</ToggleButton>
          </ToggleButtonGroup>
          <IconButton onClick={() => setTick(t => t + 1)}><RefreshIcon /></IconButton>
        </Stack>
      </Stack>

      {cargando && <LinearProgress sx={{ mb: 2 }} />}

      {/* Snapshot global */}
      {resumen && (
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <MiniStat icon={<BusinessIcon />} color="primary.main"
                      label="Empresas contratadas"
                      value={`${resumen.empresas.total} (${resumen.empresas.activas} activas)`} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MiniStat icon={<PointOfSaleIcon />} color="info.main"
                      label="Con HolaVeci Local"
                      value={resumen.empresas.conPOS} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MiniStat icon={<LocalShippingIcon />} color="success.main"
                      label="Con HolaVeci"
                      value={resumen.empresas.conHolaVeci} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MiniStat icon={<PaidIcon />} color="warning.main"
                      label={`Revenue plataforma (${dias}d)`}
                      value={fmtCOP(resumen.revenue.totalVentasPlataforma)} />
          </Grid>
        </Grid>
      )}

      {resumen && (
        <Card sx={{ mb: 3, p: 2, background: 'linear-gradient(90deg,#e8f5e9,#e3f2fd)' }}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Typography variant="caption" color="text.secondary">Solo POS</Typography>
              <Typography variant="h6" fontWeight={700}>{resumen.empresas.soloPOS}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" color="text.secondary">Solo HolaVeci</Typography>
              <Typography variant="h6" fontWeight={700}>{resumen.empresas.soloHolaVeci}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" color="text.secondary">Con ambos</Typography>
              <Typography variant="h6" fontWeight={700} color="success.main">{resumen.empresas.conAmbos}</Typography>
            </Grid>
          </Grid>
        </Card>
      )}

      {/* Filtros */}
      <Card sx={{ mb: 2, p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField fullWidth size="small"
                     placeholder="Buscar por nombre o documento"
                     value={buscar} onChange={(e) => setBuscar(e.target.value)}
                     InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
          <TextField select size="small" label="Productos" sx={{ minWidth: 220 }}
                     value={contrata} onChange={(e) => setContrata(e.target.value)}>
            <MenuItem value="">Todas</MenuItem>
            <MenuItem value="pos">Con HolaVeci Local</MenuItem>
            <MenuItem value="holaveci">Con HolaVeci</MenuItem>
            <MenuItem value="ambos">Con ambos</MenuItem>
            <MenuItem value="solo-pos">Solo POS</MenuItem>
            <MenuItem value="solo-holaveci">Solo HolaVeci</MenuItem>
          </TextField>
        </Stack>
      </Card>

      <Card>
        {empresas.length === 0 && !cargando ? (
          <Alert severity="info" sx={{ m: 2 }}>Sin empresas que coincidan con los filtros.</Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Empresa</TableCell>
                  <TableCell align="center">Estado</TableCell>
                  <TableCell align="center">Local</TableCell>
                  <TableCell align="center">HolaVeci</TableCell>
                  <TableCell align="right">Ventas del período</TableCell>
                  <TableCell align="center">Trans.</TableCell>
                  <TableCell align="center">Suc.</TableCell>
                  <TableCell align="center">Admins</TableCell>
                  <TableCell align="center">Domic.</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {empresas.map(e => (
                  <TableRow key={e.empresaId} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{e.razonSocial}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {e.nombreComercial || e.documento}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip size="small"
                            label={e.activa ? 'Activa' : 'Inactiva'}
                            color={e.activa ? 'success' : 'default'} />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={e.contrataPOS ? 'Desactivar HolaVeci Local' : 'Activar HolaVeci Local'}>
                        <Switch size="small" checked={e.contrataPOS}
                                onChange={(_, v) => cambiarPOS(e, v)} />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={e.contrataHolaVeci ? 'Desactivar HolaVeci' : 'Activar HolaVeci'}>
                        <Switch size="small" checked={e.contrataHolaVeci} color="success"
                                onChange={(_, v) => cambiarHolaVeci(e, v)} />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={500}>
                        {fmtCOP(e.metricas.ventasPeriodo)}
                      </Typography>
                      {e.metricas.ventasHolaVeci > 0 && (
                        <Typography variant="caption" color="success.main">
                          {e.metricas.ventasHolaVeci} pedidos HV
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">{fmtNum(e.metricas.transacciones)}</TableCell>
                    <TableCell align="center">{e.metricas.sucursales}</TableCell>
                    <TableCell align="center">
                      {e.metricas.admins}/{e.maxAdmins}
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="caption">
                        {e.metricas.domiciliariosPOS + e.metricas.domiciliariosHolaVeci}
                      </Typography>
                      {(e.metricas.domiciliariosPOS + e.metricas.domiciliariosHolaVeci) > 0 && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          POS:{e.metricas.domiciliariosPOS} HV:{e.metricas.domiciliariosHolaVeci}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>
    </Box>
  );
};

export default SuperadminPlataforma;
